import { SerializedSmartLink, SerializedSmartLinkStatus } from 'fym-common/src/db/serializersTypes';
import { FieldsName, FieldsType } from './consts';

/**
 * One of the artist's own pages, as the link block editor needs it: the address
 * it carries today, the name to show, and whether fans can reach it yet.
 */
export type LinkWidgetTarget = {
  id: number;
  url: string;
  name: string;
  isDraft: boolean;
};

export type LinkWidgetData = {
  url: string;
  label: string | null;
  smartLinkId?: number;
};

export function linkWidgetTargetFromSmartLink(
  smartLink: Pick<SerializedSmartLink, 'id' | 'shareUrl' | 'recentSmartLinkVersion' | 'publishedSmartLinkVersion'>
): LinkWidgetTarget {
  const version = smartLink.publishedSmartLinkVersion ?? smartLink.recentSmartLinkVersion;
  return {
    id: smartLink.id,
    url: smartLink.shareUrl ?? '',
    name: version.name,
    isDraft: smartLink.recentSmartLinkVersion.status === SerializedSmartLinkStatus.draft,
  };
}

/**
 * What the form shows for a block: the address of the page it points at, or the
 * address the artist typed. With a page picked the block borrows the page's
 * name when the artist wrote no label of their own.
 */
export function linkWidgetFormValues({
  widget,
  target,
}: {
  widget?: LinkWidgetData | null;
  target?: LinkWidgetTarget | null;
}): FieldsType {
  if (!target) {
    return {
      [FieldsName.url]: widget?.url ?? '',
      [FieldsName.label]: widget?.label ?? '',
      [FieldsName.targetSmartLinkId]: undefined,
    };
  }
  return {
    [FieldsName.url]: target.url,
    [FieldsName.label]: widget?.label ?? target.name,
    [FieldsName.targetSmartLinkId]: target.id,
  };
}

/**
 * Dropping the page reference keeps the address on screen. The artist asked for
 * a plain address, and the one they can already see is the best thing to start
 * editing from — an empty box would only make them type it again.
 */
export function clearedLinkWidgetFormValues(current: FieldsType): FieldsType {
  return {
    [FieldsName.url]: current[FieldsName.url],
    [FieldsName.label]: current[FieldsName.label],
    [FieldsName.targetSmartLinkId]: undefined,
  };
}
